import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { advertisementService } from '../services/advertisementService';
import { uploadService } from '../services/uploadService';
import ConfirmDialog from '../components/ConfirmDialog';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AdminAdvertisements = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [positionFilter, setPositionFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null, title: '', message: '', type: 'warning' });
  const [editingAd, setEditingAd] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    imageUrl: '',
    linkUrl: '',
    position: 'carousel',
    order: 0,
    isActive: true,
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    if (user === null) return;
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchAdvertisements();
  }, [user, positionFilter, isActiveFilter]);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      const response = await advertisementService.getAllAdvertisements(
        positionFilter || null,
        isActiveFilter !== '' ? isActiveFilter === 'true' : null
      );
      if (response.success) {
        setAdvertisements(response.advertisements || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des publicités:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.warning('Veuillez sélectionner un fichier image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.warning('L\'image ne doit pas dépasser 5MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      const response = await uploadService.uploadImage(formData);
      if (response.success) {
        const imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${response.imageUrl}`;
        setFormData(prev => ({ ...prev, imageUrl }));
        toast.success('Image uploadée avec succès');
      } else {
        toast.error(response.error || 'Erreur lors de l\'upload de l\'image');
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.imageUrl) {
      toast.warning('Veuillez uploader une image');
      return;
    }

    try {
      let response;
      if (editingAd) {
        response = await advertisementService.updateAdvertisement(editingAd._id, formData);
      } else {
        response = await advertisementService.createAdvertisement(formData);
      }

      if (response.success) {
        toast.success(response.message || 'Publicité enregistrée avec succès');
        resetForm();
        fetchAdvertisements();
      } else {
        toast.error(response.error || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title || '',
      imageUrl: ad.imageUrl || '',
      linkUrl: ad.linkUrl || '',
      position: ad.position || 'carousel',
      order: ad.order || 0,
      isActive: ad.isActive !== undefined ? ad.isActive : true,
      startDate: ad.startDate ? new Date(ad.startDate).toISOString().split('T')[0] : '',
      endDate: ad.endDate ? new Date(ad.endDate).toISOString().split('T')[0] : '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer la publicité',
      message: 'Êtes-vous sûr de vouloir supprimer cette publicité ?',
      type: 'danger',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await advertisementService.deleteAdvertisement(id);
          if (response.success) {
            toast.success('Publicité supprimée avec succès');
            fetchAdvertisements();
          } else {
            toast.error(response.error || 'Erreur lors de la suppression');
          }
        } catch (error) {
          console.error('Erreur:', error);
          toast.error('Erreur lors de la suppression');
        }
      },
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      imageUrl: '',
      linkUrl: '',
      position: 'carousel',
      order: 0,
      isActive: true,
      startDate: '',
      endDate: '',
    });
    setEditingAd(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="container">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Chargement...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={() => {
          if (confirmDialog.onConfirm) {
            confirmDialog.onConfirm();
          }
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      <div className="container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">📢 Gestion des Publicités</h1>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            + Ajouter une publicité
          </button>
        </div>

        {/* Filtres */}
        <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                Position
              </label>
              <select
                value={positionFilter}
                onChange={(e) => setPositionFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              >
                <option value="">Toutes</option>
                <option value="carousel">Carrousel</option>
                <option value="banner">Bannière</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                Statut
              </label>
              <select
                value={isActiveFilter}
                onChange={(e) => setIsActiveFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              >
                <option value="">Toutes</option>
                <option value="true">Actives</option>
                <option value="false">Inactives</option>
              </select>
            </div>
          </div>
        </div>

        {/* Formulaire */}
        {showForm && (
          <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
            <h2 style={{ marginBottom: '20px' }}>
              {editingAd ? 'Modifier la publicité' : 'Nouvelle publicité'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                    Titre (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                    Position *
                  </label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                    }}
                  >
                    <option value="carousel">Carrousel</option>
                    <option value="banner">Bannière</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  {formData.position === 'banner' ? 'Lien de la vidéo *' : 'Image *'}
                </label>
                {formData.position === 'banner' ? (
                  <>
                    <input
                      type="url"
                      value={formData.imageUrl}
                      onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=... ou https://example.com/video.mp4"
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                      }}
                    />
                    <p style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                      Formats supportés : Lien YouTube (youtube.com/watch?v=... ou youtu.be/...) ou lien direct vers fichier vidéo (.mp4, .webm, etc.)
                    </p>
                    {formData.imageUrl && (
                      <div style={{ marginTop: '10px' }}>
                        {(formData.imageUrl.includes('youtube.com') || formData.imageUrl.includes('youtu.be')) ? (
                          <div style={{
                            padding: '10px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            color: '#28a745',
                          }}>
                            ✓ Lien YouTube détecté
                          </div>
                        ) : formData.imageUrl.match(/\.(mp4|webm|ogg|mov|avi)$/i) ? (
                          <div style={{
                            padding: '10px',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            color: '#28a745',
                          }}>
                            ✓ Fichier vidéo détecté
                          </div>
                        ) : (
                          <div style={{
                            padding: '10px',
                            backgroundColor: '#fff3cd',
                            borderRadius: '8px',
                            fontSize: '14px',
                            color: '#856404',
                          }}>
                            ⚠ Format non reconnu. Assurez-vous que c'est un lien YouTube ou un fichier vidéo.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                      }}
                    />
                    {uploading && <p style={{ marginTop: '5px', color: '#666' }}>Upload en cours...</p>}
                    {formData.imageUrl && (
                      <div style={{ marginTop: '10px' }}>
                        <img
                          src={formData.imageUrl}
                          alt="Aperçu"
                          style={{
                            maxWidth: '300px',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Lien URL (optionnel)
                </label>
                <input
                  type="url"
                  value={formData.linkUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkUrl: e.target.value }))}
                  placeholder="https://example.com"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                    Ordre
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                    Date de début (optionnel)
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                    Date de fin (optionnel)
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  <span>Publicité active</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {editingAd ? 'Modifier' : 'Créer'}
                </button>
                <button type="button" className="btn" onClick={resetForm}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Liste des publicités */}
        <div className="card">
          <h2 style={{ marginBottom: '20px' }}>Liste des publicités ({advertisements.length})</h2>
          {advertisements.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Aucune publicité trouvée
            </p>
          ) : (
            <div style={{ display: 'grid', gap: '15px' }}>
              {advertisements.map((ad) => (
                <div
                  key={ad._id}
                  style={{
                    padding: '15px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    display: 'flex',
                    gap: '15px',
                    alignItems: 'center',
                  }}
                >
                  {ad.position === 'banner' ? (
                    <div style={{
                      width: '150px',
                      height: '100px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f0f0f0',
                      flexDirection: 'column',
                      gap: '5px',
                    }}>
                      <span style={{ fontSize: '24px' }}>🎥</span>
                      <span style={{ fontSize: '10px', textAlign: 'center', padding: '0 5px' }}>
                        {ad.imageUrl && (ad.imageUrl.includes('youtube.com') || ad.imageUrl.includes('youtu.be')) 
                          ? 'YouTube' 
                          : ad.imageUrl && ad.imageUrl.match(/\.(mp4|webm|ogg|mov|avi)$/i)
                          ? 'Vidéo'
                          : 'Lien vidéo'}
                      </span>
                    </div>
                  ) : (
                    <img
                      src={ad.imageUrl}
                      alt={ad.title || 'Publicité'}
                      style={{
                        width: '150px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <h3 style={{ marginBottom: '5px' }}>
                      {ad.title || 'Sans titre'}
                    </h3>
                    <p style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>
                      <strong>Position:</strong> {ad.position === 'carousel' ? 'Carrousel' : 'Bannière'} | 
                      <strong> Ordre:</strong> {ad.order} | 
                      <strong> Statut:</strong> {ad.isActive ? '✅ Actif' : '❌ Inactif'}
                    </p>
                    {ad.position === 'banner' && ad.imageUrl && (
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>
                        <strong>Lien vidéo:</strong> <a href={ad.imageUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{ad.imageUrl}</a>
                      </p>
                    )}
                    {ad.linkUrl && (
                      <p style={{ color: '#666', fontSize: '14px', marginBottom: '5px' }}>
                        <strong>Lien CTA:</strong> <a href={ad.linkUrl} target="_blank" rel="noopener noreferrer">{ad.linkUrl}</a>
                      </p>
                    )}
                    <p style={{ color: '#666', fontSize: '12px' }}>
                      Vues: {ad.views || 0} | Clics: {ad.clicks || 0}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className="btn"
                      onClick={() => handleEdit(ad)}
                      style={{ padding: '8px 15px' }}
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      className="btn"
                      onClick={() => handleDelete(ad._id)}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                      }}
                    >
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminAdvertisements;

